/**
 * Service Area Availability Grid Component
 *
 * Visual day-of-week selector for service area availability.
 * Shows 7 checkboxes (Monday through Sunday) for easy day selection.
 *
 * @example
 * <ServiceAreaAvailabilityGrid
 *   value={{ monday: true, tuesday: true, wednesday: false, ... }}
 *   onChange={(days) => handleChange(days)}
 *   error="At least one day must be selected"
 * />
 */

import React from 'react'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Calendar } from 'lucide-react'

interface DaysOfWeek {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

interface ServiceAreaAvailabilityGridProps {
  value: DaysOfWeek
  onChange: (value: DaysOfWeek) => void
  error?: string
  disabled?: boolean
}

const DAYS = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
] as const

export function ServiceAreaAvailabilityGrid({
  value,
  onChange,
  error,
  disabled = false,
}: ServiceAreaAvailabilityGridProps) {
  const handleDayToggle = (dayKey: keyof DaysOfWeek) => {
    onChange({
      ...value,
      [dayKey]: !value[dayKey],
    })
  }

  const handleSelectAll = () => {
    const allSelected = DAYS.every((day) => value[day.key])
    const newValue = {} as DaysOfWeek
    DAYS.forEach((day) => {
      newValue[day.key] = !allSelected
    })
    onChange(newValue)
  }

  const handleSelectWeekdays = () => {
    onChange({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    })
  }

  const handleSelectWeekends = () => {
    onChange({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: true,
      sunday: true,
    })
  }

  const selectedCount = DAYS.filter((day) => value[day.key]).length
  const allSelected = selectedCount === 7
  const weekdaysSelected =
    value.monday &&
    value.tuesday &&
    value.wednesday &&
    value.thursday &&
    value.friday &&
    !value.saturday &&
    !value.sunday

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Service Days
        </Label>
        <span className="text-sm text-muted-foreground">
          {selectedCount} {selectedCount === 1 ? 'day' : 'days'} selected
        </span>
      </div>

      {/* Quick selection buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSelectAll}
          disabled={disabled}
          className="text-xs"
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSelectWeekdays}
          disabled={disabled}
          className="text-xs"
        >
          Weekdays Only
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSelectWeekends}
          disabled={disabled}
          className="text-xs"
        >
          Weekends Only
        </Button>
      </div>

      {/* Day checkboxes grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 p-4 border rounded-lg bg-muted/30">
        {DAYS.map((day) => (
          <div
            key={day.key}
            className="flex items-center space-x-2 p-2 rounded hover:bg-background transition-colors"
          >
            <Checkbox
              id={`day-${day.key}`}
              checked={value[day.key]}
              onCheckedChange={() => handleDayToggle(day.key)}
              disabled={disabled}
            />
            <Label
              htmlFor={`day-${day.key}`}
              className="cursor-pointer select-none flex flex-col gap-1"
            >
              <span className="font-medium text-sm">{day.short}</span>
              <span className="text-xs text-muted-foreground hidden lg:inline">
                {day.label}
              </span>
            </Label>
          </div>
        ))}
      </div>

      {/* Visual indicator */}
      <div className="flex items-center gap-1">
        {DAYS.map((day) => (
          <div
            key={day.key}
            className={`flex-1 h-8 rounded flex items-center justify-center text-xs font-medium transition-colors ${
              value[day.key]
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {day.short.charAt(0)}
          </div>
        ))}
      </div>

      {/* Status message */}
      {weekdaysSelected && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-info" />
          Weekdays only (Monday - Friday)
        </div>
      )}

      {allSelected && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-success/100" />
          Available every day
        </div>
      )}

      {selectedCount === 0 && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
          No days selected - service area will be inactive
        </div>
      )}

      {/* Error message */}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
